import { PrimaryTag, Text } from "@certn/cascadia-react";
import { ActionListControl } from "@certn/forms/src/controls/ActionListControl";
import { formatDate } from "@certn/utils";
import { ArrayControlProps } from "@jsonforms/core";
import { useTranslation } from "react-i18next";

import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { updateSection, removeItems, replaceOrAddItems } from "../../utils";

/**
 * ResidencesControl component renders a list of residences with options to modify or remove items.
 *
 * @deprecated This component is deprecated and will be removed in future releases.
 * Please use the new implementation, {@link ResidencesArrayControl}, for managing residences.
 *
 * @param {ArrayControlProps} props - The properties for the control, including data and UI schema.
 * @returns The rendered residences control component.
 */
export function ResidencesControl(props: ArrayControlProps) {
  const { caseId } = useApplicationFormContext();
  const { currentSection, basePath } = useApplication();
  const { t } = useTranslation();
  const dataSection = "residences";

  const displayedDateGranularity =
    props.uischema.options?.displayedDateGranularity;

  return (
    <ActionListControl
      {...props}
      renderTag={({ end_date }) =>
        !end_date ? <PrimaryTag type="subtle">{t("Current")}</PrimaryTag> : null
      }
      renderSubtitle={({ address, start_date, end_date }) => {
        return (
          <>
            <Text style="global/body-secondary" color="text-weak">
              {[
                address.locality,
                address.administrative_division_1,
                address.country,
              ]
                .filter(Boolean)
                .join(", ")}
            </Text>
            {start_date && (
              <Text style="global/body-secondary" color="text-weak">
                {[start_date, end_date]
                  .map((date) =>
                    formatDate(
                      date,
                      displayedDateGranularity === "month"
                        ? "MMM yyyy"
                        : "dd MMM yyyy",
                      t("Present")
                    )
                  )
                  .join(" - ")}
              </Text>
            )}
          </>
        );
      }}
      onItemChanged={async (data, values, index) => {
        const updatedArray = replaceOrAddItems(
          dataSection,
          data,
          values,
          index
        );
        if (caseId) {
          await updateSection(
            caseId,
            updatedArray,
            currentSection,
            false,
            basePath
          );
        }
      }}
      onItemRemoved={async (parentValues, indexes) => {
        const updatedArray = removeItems(dataSection, parentValues, indexes);
        if (updatedArray) {
          if (caseId) {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              false,
              basePath
            );
          }
        }
      }}
    />
  );
}
